import * as z from "zod";

// Instruction schema - each task can have multiple instructions
export const JobTaskInstructionSchema = z.object({
  id: z.string().uuid(),
  job_task_id: z.string().uuid(),
  order: z.number().int().min(1, "Order must be at least 1"),
  description: z
    .string()
    .min(1, "Instruction description is required")
    .max(2000, "Instruction description must be less than 2000 characters"),
  images: z.array(z.string().url("Must be a valid URL")).optional().default([]),
  duration: z
    .number()
    .int("Duration must be a whole number")
    .min(1, "Duration must be at least 1 minute")
    .optional(),
  equipment_id: z.string().uuid().optional(),
  // References to related items for this instruction
  product_id: z.string().uuid().optional(),
  component_id: z.string().uuid().optional(),
  box_id: z.string().uuid().optional(),
  material_id: z.string().uuid().optional(),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

// Output schema - what this job task produces
export const JobTaskOutputSchema = z.object({
  id: z.string().uuid(),
  job_task_id: z.string().uuid(),
  type: z.enum(["product", "component", "box", "material", "file"], {
    required_error: "Output type is required",
  }),
  // One of these will be populated based on type
  product_id: z.string().uuid().optional(),
  component_id: z.string().uuid().optional(),
  box_id: z.string().uuid().optional(),
  material_id: z.string().uuid().optional(),
  file_url: z.string().url("Must be a valid URL").optional(),
  quantity: z
    .number()
    .int("Quantity must be a whole number")
    .min(1, "Quantity must be at least 1")
    .default(1),
  notes: z
    .string()
    .max(500, "Notes must be less than 500 characters")
    .optional(),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

// Complement schema - tasks that this job task works with/depends on
export const JobTaskComplementSchema = z.object({
  id: z.string().uuid(),
  job_task_id: z.string().uuid(),
  complement_task_id: z.string().uuid(),
  relationship_type: z.enum(["depends_on", "complements", "follows"], {
    required_error: "Relationship type is required",
  }),
  notes: z
    .string()
    .max(500, "Notes must be less than 500 characters")
    .optional(),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

// Main Job Task schema
export const JobTaskSchema = z.object({
  id: z.string().uuid(),
  job_id: z.string().uuid(),
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title must be less than 255 characters"),
  description: z
    .string()
    .max(1000, "Description must be less than 1000 characters")
    .optional(),
  order: z
    .number()
    .int("Order must be a whole number")
    .min(1, "Order must be at least 1")
    .optional(),
  // Estimated total duration (sum of all instruction durations)
  estimated_total_duration: z
    .number()
    .int("Duration must be a whole number")
    .min(1, "Duration must be at least 1 minute")
    .optional(),
  // Relations
  instructions: z.array(JobTaskInstructionSchema).default([]),
  outputs: z.array(JobTaskOutputSchema).default([]),
  complements: z.array(JobTaskComplementSchema).default([]),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

// Create schemas
export const createJobTaskInstructionSchema = JobTaskInstructionSchema.omit({
  id: true,
  created: true,
  updated: true,
});

export const createJobTaskOutputSchema = JobTaskOutputSchema.omit({
  id: true,
  created: true,
  updated: true,
});

export const createJobTaskComplementSchema = JobTaskComplementSchema.omit({
  id: true,
  created: true,
  updated: true,
});

export const createJobTaskSchema = JobTaskSchema.omit({
  id: true,
  created: true,
  updated: true,
}).extend({
  instructions: z.array(createJobTaskInstructionSchema).default([]),
  outputs: z.array(createJobTaskOutputSchema).default([]),
  complements: z.array(createJobTaskComplementSchema).default([]),
});

// Update schemas
export const updateJobTaskInstructionSchema =
  JobTaskInstructionSchema.partial().omit({
    id: true,
    job_task_id: true,
    created: true,
    updated: true,
  });

export const updateJobTaskOutputSchema = JobTaskOutputSchema.partial().omit({
  id: true,
  job_task_id: true,
  created: true,
  updated: true,
});

export const updateJobTaskComplementSchema =
  JobTaskComplementSchema.partial().omit({
    id: true,
    job_task_id: true,
    created: true,
    updated: true,
  });

export const updateJobTaskSchema = JobTaskSchema.partial()
  .omit({
    id: true,
    job_id: true,
    created: true,
    updated: true,
  })
  .extend({
    instructions: z
      .array(
        JobTaskInstructionSchema.partial().extend({
          id: z.string().uuid().optional(), // Allow for new instructions without ID
        })
      )
      .optional(),
    outputs: z
      .array(
        JobTaskOutputSchema.partial().extend({
          id: z.string().uuid().optional(), // Allow for new outputs without ID
        })
      )
      .optional(),
    complements: z
      .array(
        JobTaskComplementSchema.partial().extend({
          id: z.string().uuid().optional(), // Allow for new complements without ID
        })
      )
      .optional(),
  });

export const OutputTypeEnum = z.enum([
  "product",
  "component",
  "box",
  "material",
  "file",
]);
export const RelationshipTypeEnum = z.enum([
  "depends_on",
  "complements",
  "follows",
]);
